/**
 * Represents a musical pitch (note + octave)
 */
export interface Pitch {
  note: string;
  octave: number;
}

/**
 * Instrument types supported by the application
 */
export enum InstrumentType {
  Guitar = 'Guitar',
  Bass = 'Bass',
  Ukulele = 'Ukulele',
  Mandolin = 'Mandolin',
  Banjo = 'Banjo',
}

/**
 * Represents an instrument tuning
 */
export interface TuningDefinition {
  id: string; // e.g., "guitar-standard-e" (see TuningId and TUNING_IDS)
  label: string; // e.g., "Standard E (Guitar)"
  // Pitches for each string, ordered corresponding to visual top-to-bottom display (index 0 = topmost string).
  pitches: Pitch[];
}

/**
 * Canonical tuning identifier union.
 * Keep in sync with instrument tuning definitions.
 */
export type TuningId =
  | 'guitar-standard-e'
  | 'guitar-drop-d'
  | 'bass-standard'
  | 'ukulele-standard'
  | 'ukulele-baritone'
  | 'banjo-standard'
  | 'mandolin-standard';

/**
 * Canonical tuning identifiers map.
 * Use these instead of string literals.
 */
export const TUNING_IDS = {
  GUITAR_STANDARD_E: 'guitar-standard-e' as const,
  GUITAR_DROP_D: 'guitar-drop-d' as const,
  BASS_STANDARD: 'bass-standard' as const,
  UKULELE_STANDARD: 'ukulele-standard' as const,
  UKULELE_BARITONE: 'ukulele-baritone' as const,
  BANJO_STANDARD: 'banjo-standard' as const,
  MANDOLIN_STANDARD: 'mandolin-standard' as const,
} as const;

/**
 * Represents an instrument and its default properties
 */
export interface InstrumentDefinition {
  type: InstrumentType;
  name: string;
  defaultNumFrets: number;
  defaultTunings: TuningDefinition[];
}

/**
 * Represents scale degree information
 */
export interface ScaleDegreeInfo {
  degree: number; // Base degree (1-7)
  alteration: string; // "", "b", "#", "bb", "##"
  display: string; // Full display (e.g., "b3", "5", "#4")
  semitones: number; // Semitones from root
}

/**
 * Interface for special scale information
 */
export interface SpecialScaleInfo {
  degrees: ScaleDegreeInfo[]; // Custom degree information
}

/**
 * Common scale types - for reference and autocomplete
 * These are standard values that can be passed to TonalJS
 */
export const ScaleNames = {
  Major: {
    value: 'major',
    displayName: 'Major',
  },
  Minor: {
    value: 'minor',
    displayName: 'Minor',
  },
  Dorian: {
    value: 'dorian',
    displayName: 'Dorian',
  },
  Phrygian: {
    value: 'phrygian',
    displayName: 'Phrygian',
  },
  Lydian: {
    value: 'lydian',
    displayName: 'Lydian',
  },
  Mixolydian: {
    value: 'mixolydian',
    displayName: 'Mixolydian',
  },
  Locrian: {
    value: 'locrian',
    displayName: 'Locrian',
  },
  MelodicMinor: {
    value: 'melodic minor',
    displayName: 'Melodic Minor',
  },
  HarmonicMinor: {
    value: 'harmonic minor',
    displayName: 'Harmonic Minor',
  },
  MajorPentatonic: {
    value: 'major pentatonic',
    displayName: 'Major Pentatonic',
  },
  MinorPentatonic: {
    value: 'minor pentatonic',
    displayName: 'Minor Pentatonic',
  },
  Blues: {
    value: 'blues',
    displayName: 'Blues',
  },
  // Add more as needed
} as const;

/**
 * Common chord types - for reference and autocomplete
 * These are standard values that can be passed to TonalJS
 */
export const ChordNames = {
  Major: 'major',
  Minor: 'minor',
  Diminished: 'diminished',
  Augmented: 'augmented',
  Sus2: 'sus2',
  Sus4: 'sus4',
  Major7: 'maj7',
  Minor7: 'm7',
  Dominant7: '7',
  Diminished7: 'dim7',
  // Add more as needed
} as const;
